import { useMsal } from '@azure/msal-react';
import { Button, Search } from '@consigli/facade';
import { useGetMeetingsQuery, useProjectId } from '@consigli/hooks';
import { Meeting } from '@consigli/types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner } from 'react-icons/fa';

import { DeleteConfirmModal } from './components/delete-confirm-modal';
import { formatMeetingDate, formatMeetingTime } from './format-meeting-datetime';
import { useMeetingCreation, useMeetingDeletion, useMeetingSync, useOutlookImport } from './hooks';
import { MeetingFilters, MeetingFilterValues } from './meeting-filters';
import { MeetingCard } from './meeting-secretary-card';
import { MeetingInvitationModal } from './meeting-secretary-invitation-modal';
import { MeetingSeriesAccordion } from './meeting-series-accordion';
import { OutlookImportModal } from './outlook-import/outlook-import-modal';
import { useGroupedMeetings } from './use-grouped-meetings';
import { getNonOrganizerParticipants } from './utils/export-types';
import { getTranscriptStatus } from './utils/transcript-status';

interface RecentMeetingsProps {
  onMeetingSelect?: (meeting: Meeting) => void;
}

export const RecentMeetings = ({ onMeetingSelect }: RecentMeetingsProps) => {
  const { t, i18n } = useTranslation();
  const { accounts } = useMsal();
  const projectId = useProjectId();

  const [searchText, setSearchText] = useState('');
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [showOutlookImport, setShowOutlookImport] = useState(false);
  const [filters, setFilters] = useState<MeetingFilterValues>({});

  const isAuthenticated = accounts.length > 0;

  // Use RTK Query hook for fetching meetings with filters
  const queryParams = {
    projectId,
    startDate: filters.startDate,
    endDate: filters.endDate,
  };

  const {
    data: meetingsResponse,
    isLoading: isMeetingsLoading,
    isUninitialized: isMeetingsUninitialized,
    isFetching: isMeetingsFetching,
  } = useGetMeetingsQuery(queryParams);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  // Extract meetings array from RTK Query response (before any early returns)
  const meetingsArray = useMemo(() => {
    if (!meetingsResponse) return [];
    const meetings = Array.isArray(meetingsResponse)
      ? meetingsResponse
      : meetingsResponse?.meetings || [];

    return meetings;
  }, [meetingsResponse]);

  useMeetingSync({
    projectId,
    meetings: meetingsArray,
    isAuthenticated,
    isMeetingsLoading,
  });

  // Filter meetings by search text
  const filteredMeetings = useMemo(() => {
    return meetingsArray.filter((meeting: Meeting) => {
      const searchLower = searchText.toLowerCase();
      return (
        meeting.title?.toLowerCase().includes(searchLower) ||
        meeting.organizerEmail?.toLowerCase().includes(searchLower)
      );
    });
  }, [meetingsArray, searchText]);

  // Group meetings by series
  const groupedMeetings = useGroupedMeetings(filteredMeetings);

  const existingCalendarEventIds = useMemo(() => {
    const ids = new Set<string>();
    for (const meeting of meetingsArray) {
      if (meeting.calendarEventId) ids.add(meeting.calendarEventId);
    }
    return ids;
  }, [meetingsArray]);

  const { createMeeting, loading, error } = useMeetingCreation({
    projectId,
    onSuccess: () => setShowCreateMeeting(false),
  });

  const { importOutlook, isOutlookImporting, isImporting } = useOutlookImport({
    projectId,
    onImportStart: () => setShowOutlookImport(false),
  });

  const {
    confirmDeleteMeeting,
    setConfirmDeleteMeeting,
    confirmDeleteSeries,
    setConfirmDeleteSeries,
    handleDeleteMeeting,
    handleDeleteMeetingWithCalendar,
    handleDeleteSeries,
    isMeetingCreator,
    canDeleteMeetingCalendar,
    isDeletingOthersMeeting,
    canDeleteSeriesCalendar,
    isDeletingOthersSeries,
  } = useMeetingDeletion({ projectId });

  // Early returns after all hooks are called
  if (isMeetingsLoading || isMeetingsUninitialized) {
    return <div>{t('meeting-secretary.meetings.loading')}</div>;
  }
  if (error) return <div>{t('meeting-secretary.meetings.error', { error })}</div>;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <Search
            text={searchText}
            onChange={handleSearchChange}
            placeholder={t('meeting-secretary.meetings.search-placeholder')}
            className="w-64"
          />
          <div className="flex items-center gap-3">
            <Button
              secondary
              onClick={() => setShowOutlookImport(true)}
              rounded
              disabled={!isAuthenticated || isOutlookImporting}
            >
              {isOutlookImporting ? (
                <span className="flex items-center gap-2">
                  <FaSpinner className="animate-spin" size={14} />
                  {t('meeting-secretary.outlook-import.importing')}
                </span>
              ) : (
                t('meeting-secretary.header.import-from-outlook')
              )}
            </Button>
            <Button primary onClick={() => setShowCreateMeeting(true)} rounded disabled={loading}>
              {loading
                ? t('meeting-secretary.meetings.creating')
                : t('meeting-secretary.header.schedule-meeting')}
            </Button>
          </div>
        </div>

        <MeetingFilters filters={filters} onFiltersChange={setFilters} />
      </div>

      <div className="space-y-4">
        {filteredMeetings && filteredMeetings.length > 0 ? (
          <>
            {/* Recurring Series Groups */}
            {groupedMeetings.series.map((series) => (
              <MeetingSeriesAccordion
                key={series.seriesId}
                series={series}
                onMeetingSelect={onMeetingSelect}
                onDeleteSeries={(s) => setConfirmDeleteSeries(s)}
              />
            ))}

            {/* Standalone Meetings */}
            {groupedMeetings.standalone.length > 0 && (
              <>
                {groupedMeetings.standalone.map((meeting: Meeting) => (
                  <MeetingCard
                    key={meeting.id}
                    meetingId={meeting.id}
                    title={meeting.title}
                    company={meeting.organizerEmail}
                    date={formatMeetingDate(meeting.scheduledStartTime, i18n.language)}
                    time={formatMeetingTime(
                      meeting,
                      i18n.language,
                      t('meeting-secretary.meetings.minutes'),
                    )}
                    participants={t('meeting-secretary.meetings.participants-count', {
                      count: getNonOrganizerParticipants(meeting.participants).length,
                    })}
                    onSelect={() => onMeetingSelect?.(meeting)}
                    isRecurring={false}
                    transcriptStatus={getTranscriptStatus(meeting)}
                    onDelete={() => setConfirmDeleteMeeting(meeting)}
                  />
                ))}
              </>
            )}
          </>
        ) : !isMeetingsFetching ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {searchText
                ? `${t('meeting-secretary.meetings.no-results')} ${searchText}`
                : t('meeting-secretary.meetings.no-results')}
            </p>
          </div>
        ) : null}
      </div>

      <MeetingInvitationModal
        isOpen={showCreateMeeting}
        onClose={() => setShowCreateMeeting(false)}
        onCreateMeeting={createMeeting}
        loading={loading}
        height="75vh"
        projectId={projectId}
      />

      <OutlookImportModal
        isOpen={showOutlookImport}
        onClose={() => setShowOutlookImport(false)}
        onImport={importOutlook}
        isImporting={isImporting}
        existingCalendarEventIds={existingCalendarEventIds}
      />

      <DeleteConfirmModal
        isOpen={confirmDeleteMeeting !== null}
        onClose={() => setConfirmDeleteMeeting(null)}
        onConfirm={() => {
          const meeting = confirmDeleteMeeting;
          setConfirmDeleteMeeting(null);
          if (!meeting) return;
          if (meeting.calendarEventId && isMeetingCreator(meeting)) {
            handleDeleteMeetingWithCalendar(meeting);
          } else {
            handleDeleteMeeting(meeting);
          }
        }}
        titleKey="meeting-secretary.meetings.delete-meeting"
        messageKey={
          canDeleteMeetingCalendar
            ? 'meeting-secretary.meetings.delete-confirm-calendar'
            : isDeletingOthersMeeting
              ? 'meeting-secretary.meetings.delete-confirm-participant'
              : 'meeting-secretary.meetings.delete-confirm'
        }
        confirmLabelKey={
          canDeleteMeetingCalendar
            ? 'meeting-secretary.meetings.delete-app-and-outlook'
            : isDeletingOthersMeeting
              ? 'meeting-secretary.meetings.delete-remove-from-app'
              : 'meeting-secretary.meetings.delete'
        }
        secondaryConfirmLabelKey={
          canDeleteMeetingCalendar ? 'meeting-secretary.meetings.delete-app-only' : undefined
        }
        onSecondaryConfirm={
          canDeleteMeetingCalendar
            ? () => {
                const meeting = confirmDeleteMeeting;
                setConfirmDeleteMeeting(null);
                if (meeting) handleDeleteMeeting(meeting);
              }
            : undefined
        }
      />

      <DeleteConfirmModal
        isOpen={confirmDeleteSeries !== null}
        onClose={() => setConfirmDeleteSeries(null)}
        onConfirm={() => {
          const series = confirmDeleteSeries;
          setConfirmDeleteSeries(null);
          if (!series) return;
          handleDeleteSeries(series.seriesIds, series.title, canDeleteSeriesCalendar);
        }}
        titleKey="meeting-secretary.meetings.delete-series"
        messageKey={
          canDeleteSeriesCalendar
            ? 'meeting-secretary.meetings.delete-series-confirm-calendar'
            : isDeletingOthersSeries
              ? 'meeting-secretary.meetings.delete-series-confirm-participant'
              : 'meeting-secretary.meetings.delete-series-confirm'
        }
        confirmLabelKey={
          canDeleteSeriesCalendar
            ? 'meeting-secretary.meetings.delete-app-and-outlook'
            : isDeletingOthersSeries
              ? 'meeting-secretary.meetings.delete-remove-from-app'
              : 'meeting-secretary.meetings.delete'
        }
        secondaryConfirmLabelKey={
          canDeleteSeriesCalendar ? 'meeting-secretary.meetings.delete-app-only' : undefined
        }
        onSecondaryConfirm={
          canDeleteSeriesCalendar
            ? () => {
                const series = confirmDeleteSeries;
                setConfirmDeleteSeries(null);
                if (series) handleDeleteSeries(series.seriesIds, series.title, false);
              }
            : undefined
        }
      />
    </div>
  );
};
